"use client";
import { useState, useEffect, useCallback } from 'react';
import { courseListsApi } from '@/app/services/api';
import { CourseList } from '@/app/types/course';
import { useAuth } from '@/app/Components/AuthProvider';

export function useCourseLists() {
  const [courseLists, setCourseLists] = useState<CourseList[]>([]);
  const [officialCourseLists, setOfficialCourseLists] = useState<CourseList[]>([]);
  const [allCourseLists, setAllCourseLists] = useState<CourseList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const loadCourseLists = useCallback(async () => {
    if (!user?.id) {
      setCourseLists([]);
      setAllCourseLists([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      console.log('🔄 Loading course lists for user:', user.id);
      const [myLists, officialLists] = await Promise.all([
        courseListsApi.get_all_user_course_lists(user.id) as Promise<CourseList[]>,
        courseListsApi.get_official_course_lists() as Promise<CourseList[]>
      ]);
      
      console.log('📚 Course lists loaded:', {
        userLists: myLists?.length || 0,
        officialLists: officialLists?.length || 0,
        userListsData: myLists
      });
      
      setCourseLists(myLists || []);
      setOfficialCourseLists(officialLists || []);
      setAllCourseLists([...(myLists || []), ...(officialLists || [])]);
    } catch (err) {
      console.error('Error loading course lists:', err);
      setError('Failed to load course lists');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const createCourseList = useCallback(async (listData: any) => {
    if (!user?.id) return null;
    
    try {
      console.log('🆕 Creating course list:', listData);
      const newList = await courseListsApi.create({
        ...listData,
        user_generated: user.id
      });
      
      console.log('✅ Course list created:', newList);
      
      // Force reload to ensure we get the latest data
      await loadCourseLists();
      
      return newList;
    } catch (err) {
      console.error('Error creating course list:', err);
      setError('Failed to create course list');
      throw err;
    }
  }, [user?.id, loadCourseLists]);

  const updateCourseList = useCallback(async (listId: number, updates: any) => {
    try {
      await courseListsApi.update(listId, updates);
      // Force reload to ensure we get the latest data
      await loadCourseLists();
    } catch (err) {
      console.error('Error updating course list:', err);
      setError('Failed to update course list');
      throw err;
    }
  }, [loadCourseLists]);

  const deleteCourseList = useCallback(async (listId: number) => {
    try {
      await courseListsApi.delete(listId);
      // Force reload to ensure we get the latest data
      await loadCourseLists();
    } catch (err) {
      console.error('Error deleting course list:', err);
      setError('Failed to delete course list');
      throw err;
    }
  }, [loadCourseLists]);

  // Load course lists when user changes
  useEffect(() => {
    loadCourseLists();
  }, [loadCourseLists]);

  return {
    courseLists, // User's course lists only
    officialCourseLists, // Official course lists only
    allCourseLists, // Combined user and official course lists
    isLoading,
    error,
    loadCourseLists,
    createCourseList,
    updateCourseList,
    deleteCourseList,
    clearError: () => setError('')
  };
}
